'use strict';

/* Frenly Bricks — PDP circular magnifier lens.
 *
 * Ports the auction lot viewer's magnifier interaction (see
 * snippets/frenly-ebay-auctions-js-full-2.liquid, the mousemove handler
 * that positions span.frenly-auctions__lot-magnifier) onto the standard
 * Dawn product gallery, which has a different DOM shape:
 *
 *   media-gallery
 *     ul.product__media-list
 *       li.product__media-item          (one per photo; Dawn shows only
 *                                         the .is-active one on desktop via
 *                                         `display:none` on the rest)
 *         .product-media-container
 *           .product__modal-opener--image   (click target for the lightbox;
 *             .product__media.media           an invisible full-size button
 *               img                           layers on top of the image —
 *             .product__media-toggle          see product__media-toggle::after
 *           )                                 in section-main-product.css)
 *
 * Because .product__media-toggle overlays the image to catch clicks (it
 * opens the existing lightbox modal — left completely alone here), a
 * mousemove listener on .product__media itself would miss events that
 * land on the toggle button. Dawn's own assets/magnify.js works around
 * this by listening on the shared ancestor, .product__modal-opener--image,
 * and doing the same here keeps this change consistent with (and safe
 * alongside) that existing pattern. Each gallery photo gets its own lens
 * appended into its own .product__media box (which already has
 * position:relative; overflow:hidden — see .media in assets/base.css — so
 * the lens is clipped at the image edge exactly like on the auction page).
 * Since Dawn never swaps the <img> src when switching photos — it just
 * toggles which .product__media-item is visible — every image gets its
 * own listener once, up front; no re-attaching on thumbnail/slide change
 * is needed, and hidden slides simply cannot receive mouse events.
 *
 * FIX 2026-09-04: bug pack item 5e. Marshall reported the lens felt like it
 * took "about a minute" to show anything the first time he hovered a photo.
 * Two separate causes, both fixed below:
 *
 *   1. ensureZoomSrc() used to run for the first time on the very first
 *      mousemove, and always picked the LARGEST candidate in the image's
 *      srcset (often ~1946px wide for our product photography). So the
 *      first hover kicked off a big, uncached download and the lens sat
 *      empty (transparent) until it finished. Fixed by (a) capping the
 *      requested zoom width to a sane MAX_ZOOM_WIDTH instead of always the
 *      absolute largest candidate, and (b) warming the browser cache for
 *      that capped image as soon as the gallery is set up (pointerenter/
 *      setup time), not on first mousemove.
 *   2. The lens had nothing to show at all until the hi-res image finished
 *      loading, even though the already-loaded, on-screen <img> (img.src /
 *      img.currentSrc) was available immediately. The lens now seeds its
 *      background with that already-loaded image right away, then swaps to
 *      the capped hi-res version the instant it finishes downloading (or
 *      immediately, if the browser already has it cached from the warm-up
 *      above) -- so the lens never has to show a blank circle.
 *
 * Also see templates/product.json, where section.settings.image_zoom was
 * switched from "hover" to "lightbox": that setting controls whether Dawn's
 * own assets/magnify.js (assets/magnify.js, EnableZoomOnHover) is loaded on
 * this template at all (sections/main-product.liquid). With it set to
 * "hover" that second hover-zoom system loaded on every PDP alongside this
 * one — harmless only by accident, because nothing on this page currently
 * carries the `.image-magnify-hover` class that script looks for. That is
 * one HTML change away from silently reintroducing a second, competing lens
 * on hover. "lightbox" keeps Dawn's click-to-expand modal (untouched by
 * this file) while guaranteeing only this custom lens ever runs on hover.
 */
(function () {
  var ZOOM_FACTOR = 2.45;
  var LENS_RADIUS = 95; // half of the 190px lens in frenly-pdp-magnifier.css
  var MAX_ZOOM_WIDTH = 1600; // cap the hi-res zoom source instead of always using the largest srcset candidate

  function desktopHover() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function bestZoomSrc(img) {
    if (img.dataset.zoomSrc) return img.dataset.zoomSrc;
    var candidates = (img.getAttribute('srcset') || '')
      .split(',')
      .map(function (candidate) {
        var parts = candidate.trim().split(/\s+/);
        return { url: parts[0], width: parseInt(parts[1], 10) || 0 };
      })
      .filter(function (candidate) {
        return candidate.url;
      });
    candidates.sort(function (a, b) {
      return b.width - a.width;
    });
    // Prefer the largest candidate that still fits under MAX_ZOOM_WIDTH; if
    // every candidate is bigger than the cap (unusually large source photo),
    // fall back to the smallest one available rather than the biggest.
    var capped = null;
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].width <= MAX_ZOOM_WIDTH) {
        capped = candidates[i];
        break;
      }
    }
    if (!capped) capped = candidates[candidates.length - 1];
    return (capped && capped.url) || img.currentSrc || img.src;
  }

  function setupOpener(opener) {
    if (opener.dataset.pdpMagnifierReady === 'true') return;
    var media = opener.querySelector('.product__media');
    var img = media && media.querySelector('img');
    if (!media || !img) return;
    opener.dataset.pdpMagnifierReady = 'true';

    var lens = document.createElement('span');
    // The "zoom" class is not decorative — it opts this element out of
    // Dawn's `.media > :not(.zoom):not(.deferred-media__poster-button)`
    // rule (assets/base.css), which forces every other direct child of
    // .media to `position:absolute; inset:0; width/height:100%`. That
    // rule wins on specificity (0,3,0, from its two :not() pseudo-classes)
    // over a plain `.frenly-pdp__lot-magnifier{width:190px;height:190px}`
    // (0,1,0) regardless of stylesheet order, so without this the lens
    // silently got stretched to fill the whole image box and its
    // `display:none` default got forced to `display:block` — invisible
    // as a "lens" but present, and permanently visible. Confirmed via
    // getComputedStyle before adding this class.
    lens.className = 'frenly-pdp__lot-magnifier zoom';
    lens.setAttribute('aria-hidden', 'true');
    media.appendChild(lens);

    var zoomSrc = null;
    var hiResReady = false;

    function seedPlaceholder() {
      // Show *something* immediately: the already-loaded, on-screen image
      // (whatever resolution the browser picked for the visible <img>),
      // so the lens is never a blank circle while the hi-res version loads.
      if (hiResReady) return;
      var placeholder = img.currentSrc || img.src;
      if (placeholder) {
        lens.style.backgroundImage = 'url("' + placeholder.replace(/"/g, '%22') + '")';
      }
    }

    function warmZoomSrc() {
      var src = bestZoomSrc(img);
      if (!src || src === zoomSrc) return;
      zoomSrc = src;
      hiResReady = false;
      seedPlaceholder();
      // Preload via a detached Image() so the swap to the capped hi-res
      // background only happens once it is actually decodable, but the
      // request starts now (pointerenter / gallery-ready) instead of
      // waiting for the first mousemove -- that's the "about a minute"
      // fix. If the browser already cached this URL, onload fires almost
      // immediately.
      var preload = new Image();
      preload.onload = function () {
        if (zoomSrc !== src) return; // superseded by a newer call, ignore
        hiResReady = true;
        lens.style.backgroundImage = 'url("' + src.replace(/"/g, '%22') + '")';
      };
      preload.src = src;
    }

    if (img.complete) warmZoomSrc();
    img.addEventListener('load', warmZoomSrc);
    // Warm up as soon as the pointer arrives, well before the first
    // mousemove would have fired ensureZoomSrc under the old code.
    opener.addEventListener('pointerenter', warmZoomSrc);

    opener.addEventListener('mousemove', function (event) {
      if (!desktopHover()) return;

      var rect = img.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      var naturalWidth = img.naturalWidth || rect.width;
      var naturalHeight = img.naturalHeight || rect.height;
      var fit = Math.min(rect.width / naturalWidth, rect.height / naturalHeight);
      var displayedWidth = naturalWidth * fit;
      var displayedHeight = naturalHeight * fit;
      var imageLeft = rect.left + (rect.width - displayedWidth) / 2;
      var imageTop = rect.top + (rect.height - displayedHeight) / 2;

      if (
        event.clientX < imageLeft ||
        event.clientX > imageLeft + displayedWidth ||
        event.clientY < imageTop ||
        event.clientY > imageTop + displayedHeight
      ) {
        media.classList.remove('is-pdp-magnifying');
        return;
      }

      if (!zoomSrc) warmZoomSrc(); // safety net if pointerenter was somehow missed
      var mediaRect = media.getBoundingClientRect();
      var x = (event.clientX - imageLeft) / displayedWidth;
      var y = (event.clientY - imageTop) / displayedHeight;

      lens.style.left = (event.clientX - mediaRect.left) + 'px';
      lens.style.top = (event.clientY - mediaRect.top) + 'px';
      lens.style.backgroundSize = (displayedWidth * ZOOM_FACTOR) + 'px ' + (displayedHeight * ZOOM_FACTOR) + 'px';
      lens.style.backgroundPosition =
        (LENS_RADIUS - x * displayedWidth * ZOOM_FACTOR) + 'px ' +
        (LENS_RADIUS - y * displayedHeight * ZOOM_FACTOR) + 'px';
      media.classList.add('is-pdp-magnifying');
    });

    opener.addEventListener('mouseleave', function () {
      media.classList.remove('is-pdp-magnifying');
    });
  }

  function init(root) {
    if (!desktopHover()) return;
    (root || document).querySelectorAll('media-gallery .product__modal-opener--image').forEach(setupOpener);
  }

  init();

  // Safety net for galleries whose media list is (re)built after this
  // script runs (e.g. a variant swap that re-renders media). Dawn's own
  // gallery slide-switching only toggles the `is-active` class, which
  // this observer ignores (setupOpener no-ops on already-wired openers),
  // so this only does real work when new <img> elements actually appear.
  document.querySelectorAll('media-gallery').forEach(function (gallery) {
    var observer = new MutationObserver(function () {
      init(gallery);
    });
    observer.observe(gallery, { childList: true, subtree: true });
  });
})();
